import { FlagKey } from '../types/Flag';
import { LoggerListItem } from '../types/Logger';

/**
 * Extracts and normalizes flag keys from a logger's status.
 * Converts all flag keys from kebab-case to camelCase format.
 * Returns empty array if logger has no flags.
 *
 * @param logger - The logger item to extract flags from
 * @returns Array of normalized flag keys in camelCase format
 *
 * @example
 * extractLoggerFlags(logger)  // ['service.endOfLife', 'service.batteryReplacement']
 */
export const extractLoggerFlags = (logger: LoggerListItem): FlagKey[] => {
    const flagsArr = logger.status?.flags?.details?.flags ?? [];
    return flagsArr.map((flag) => convertFlagKeyToCamelCase(flag.key) as FlagKey);
};

/**
 * Converts flag keys from kebab-case to camelCase (e.g., "service.end-of-life" → "service.endOfLife").
 * Idempotent - safe to call on already camelCased keys.
 * Returns input unchanged if not in "prefix.suffix" format.
 *
 * @example
 * convertFlagKeyToCamelCase('service.end-of-life')  // 'service.endOfLife'
 * convertFlagKeyToCamelCase('service.endOfLife')    // 'service.endOfLife'
 */
export const convertFlagKeyToCamelCase = (flagKey: string): string => {
    const parts = flagKey.split('.');
    if (parts.length !== 2) return flagKey;

    const [prefix, suffix] = parts;
    const camelCaseSuffix = suffix.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());

    return `${prefix}.${camelCaseSuffix}`;
};
